import { PHOTO_JPEG_QUALITY, PHOTO_MAX_SIDE_PX, PHOTO_TARGET_BYTES } from '@pms/shared';
import imageCompression from 'browser-image-compression';

import { drawStamp, type StampContext } from './stamp.js';

/**
 * Подготовка снимка к отправке.
 *
 * Оригинал не хранится и не отправляется: с телефона уходит только сжатый кадр
 * со штампом. На слабом мобильном интернете разница между 4 МБ и 300 КБ —
 * это разница между «отчёт ушёл» и «отчёт не ушёл».
 */

export interface PreparedPhoto {
  blob: Blob;
  bytes: number;
  width: number;
  height: number;
  sha256: string;
}

async function loadBitmap(blob: Blob): Promise<{ width: number; height: number; source: CanvasImageSource }> {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(blob);
    return { width: bitmap.width, height: bitmap.height, source: bitmap };
  }

  // Запасной путь для старых движков, где createImageBitmap нет
  const url = URL.createObjectURL(blob);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('Не удалось прочитать снимок'));
      element.src = url;
    });
    return { width: image.naturalWidth, height: image.naturalHeight, source: image };
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function sha256Hex(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

export async function preparePhoto(file: Blob, context: StampContext): Promise<PreparedPhoto> {
  const compressed = await imageCompression(file as File, {
    maxWidthOrHeight: PHOTO_MAX_SIDE_PX,
    maxSizeMB: PHOTO_TARGET_BYTES / (1024 * 1024),
    initialQuality: PHOTO_JPEG_QUALITY,
    useWebWorker: true,
    fileType: 'image/jpeg',
  });

  const { width, height, source } = await loadBitmap(compressed);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Браузер не поддерживает обработку изображений');

  ctx.drawImage(source, 0, 0, width, height);
  drawStamp(ctx, width, height, context);

  const stamped = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', PHOTO_JPEG_QUALITY);
  });
  if (!stamped) throw new Error('Не удалось подготовить снимок');

  return {
    blob: stamped,
    bytes: stamped.size,
    width,
    height,
    sha256: await sha256Hex(stamped),
  };
}
